import React from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameMonth,
} from "date-fns";

const EventCalendar = ({
  events = [],
  highlightedDate,
  onHighlightDate,
  currentDate,
  onDateChange,
}) => {
  const normalize = (d) => {
    const date = new Date(d);
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  };

  const handlePrevMonth = () => onDateChange?.(subMonths(currentDate, 1));
  const handleNextMonth = () => onDateChange?.(addMonths(currentDate, 1));
  const handleResetToday = () => onDateChange?.(new Date());

  const handleDateClick = (date) => {
    const target = normalize(date);
    onHighlightDate?.(target);

    const el = document.querySelector(`[data-event-date='${target}']`);
    if (el && window.innerWidth < 768) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const renderHeader = () => (
    <div className="calendar-header" role="navigation" aria-label="Calendar navigation">
      <button
        className="calendar-today"
        onClick={handleResetToday}
        aria-label="Go to today"
      >
        Today
      </button>
      <div className="calendar-nav-group">
        <button
          className="calendar-nav"
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <h3 className="calendar-month">{format(currentDate, "MMMM yyyy")}</h3>
        <button
          className="calendar-nav"
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
    </div>
  );

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(new Date());
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="calendar-day-name" key={i}>
          {format(addDays(startDate, i), "EEE")}
        </div>
      );
    }
    return <div className="calendar-days-row">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const dayKey = normalize(cloneDay);
        let hasEvent = false;
        let isMultiDay = false;

        for (const e of events) {
          const start = normalize(e.date);
          const end = e.end ? normalize(e.end) : start;
          if (dayKey >= start && dayKey <= end) {
            hasEvent = true;
            if (end !== start) {
              isMultiDay = true;
            }
            break;
          }
        }

        const isSelected = highlightedDate === dayKey;
        const isInMonth = isSameMonth(day, monthStart);
        const classNames = [
          "calendar-cell",
          !isInMonth && "disabled",
          hasEvent && (isMultiDay ? "calendar-fill multi" : "calendar-fill single"),
          isSelected && "calendar-selected",
        ]
          .filter(Boolean)
          .join(" ");

        const ariaLabelParts = [`${format(cloneDay, "MMMM d, yyyy")}`];
        if (hasEvent) {
          ariaLabelParts.push(isMultiDay ? "Multi-day event" : "Single-day event");
        }

        days.push(
          <div
            className={classNames}
            key={day}
            tabIndex={hasEvent ? 0 : -1}
            role={hasEvent ? "button" : undefined}
            aria-label={ariaLabelParts.join(" — ")}
            onClick={() => hasEvent && handleDateClick(cloneDay)}
            data-event-date={hasEvent ? dayKey : undefined}
          >
            <span>{format(cloneDay, "d")}</span>
          </div>
        );

        day = addDays(day, 1);
      }
      rows.push(
        <div className="calendar-row" key={day}>
          {days}
        </div>
      );
      days = [];
    }

    return <div className="calendar-body">{rows}</div>;
  };

  const renderLegend = () => (
    <div className="calendar-legend" role="list" aria-label="Event appearance legend">
      <div className="calendar-legend-item" role="listitem" aria-label="Single-day appearance">
        <span className="calendar-legend-dot single" aria-hidden="true" />
        Single-day appearance
      </div>
      <div className="calendar-legend-item" role="listitem" aria-label="Multi-day appearance">
        <span className="calendar-legend-dot multi" aria-hidden="true" />
        Multi-day appearance
      </div>
    </div>
  );

  return (
    <section className="event-calendar" role="region" aria-label="Event calendar">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
      {renderLegend()}
    </section>
  );
};

export default EventCalendar;
